import { computed, inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';

import { SurveyInstanceSignalStateType } from '../../types';
import { SurveyService } from '../../services/survey.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { delay, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';

import { surveySectionAnswerTracker } from '../../utils';

export const SURVEY_INSTANCE_SIGNAL_STATE =
  signalState<SurveyInstanceSignalStateType>({
    answerTracker: null,
    error: null,
    isLoading: false,
    surveyInstance: null,
  });

@Injectable()
export class SurveyInstanceSignalState {
  private readonly _surveySignalState = SURVEY_INSTANCE_SIGNAL_STATE;
  private readonly _surveyService = inject(SurveyService);

  $surveyInstance = computed(() => this._surveySignalState.surveyInstance());
  $answerTracker = computed(() => this._surveySignalState.answerTracker());

  loadSurveyInstance = rxMethod<{ workspaceId: string }>(
    pipe(
      tap(() =>
        patchState(this._surveySignalState, {
          error: null,
          isLoading: true,
        }),
      ),
      switchMap(({ workspaceId }) =>
        this._surveyService.getSurveyInstance(workspaceId).pipe(
          delay(300), // Simulate network delay and showing skeleton loader
          tapResponse({
            error: (error: Error) =>
              patchState(this._surveySignalState, {
                answerTracker: null,
                error,
                isLoading: false,
                surveyInstance: null,
              }),
            finalize: () =>
              patchState(this._surveySignalState, { isLoading: false }),
            next: (response) => {
              const _sections = response.data?.children;
              const _answerTracker = _sections
                ? surveySectionAnswerTracker(_sections)
                : null;
              patchState(this._surveySignalState, {
                answerTracker: _answerTracker,
                error: null,
                surveyInstance: response,
              });
            },
          }),
        ),
      ),
    ),
  );

  updateSurveyInstance = (
    surveyInstance: SurveyInstanceSignalStateType['surveyInstance'],
  ) => {
    patchState(this._surveySignalState, {
      surveyInstance,
    });
  };
}
